import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '@/api/client';
import { Link2, ArrowRight, AlertCircle, Loader2, HelpCircle } from 'lucide-react';

export default function Register() {
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCode.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await api.post<{ user_id: string; redirect_url: string }>(
        '/api/v1/auth/register',
        { invite_code: inviteCode.trim().toUpperCase() },
      );
      // Redirect to GitHub OAuth with register token
      window.location.href = data.redirect_url;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.body.message ?? 'Invalid invite code');
      } else {
        setError('Something went wrong. Please try again.');
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f9fb] flex flex-col">
      {/* Header */}
      <header className="w-full">
        <div className="mx-auto max-w-[1280px] px-[24px] py-[32px] flex items-center justify-between">
          <div className="flex items-center gap-[10px]">
            <div className="w-[40px] h-[40px] bg-[#4648d4] rounded-[8px]" />
            <div className="flex flex-col">
              <span className="font-semibold text-[20px] tracking-[-1px] text-[#191c1e] leading-tight">
                Cadence
              </span>
              <span className="text-[10px] uppercase tracking-[1px] text-[#464554] leading-tight">
                English Learning OS
              </span>
            </div>
          </div>
          <div className="flex items-center gap-[6px] text-[#464554]">
            <span className="text-[14px]">Support</span>
            <HelpCircle className="w-[16px] h-[16px]" />
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-[16px] pb-[80px]">
        {/* White card */}
        <div className="w-full max-w-[448px] bg-white rounded-[12px] shadow-[0px_32px_64px_-12px_rgba(25,28,30,0.06)] p-[40px]">
          <div className="flex flex-col items-center">
            {/* Icon */}
            <div className="w-[64px] h-[64px] bg-[#f2f4f6] rounded-full flex items-center justify-center mb-[24px]">
              <Link2 className="w-[28px] h-[28px] text-[#464554]" />
            </div>

            {/* Heading */}
            <h1 className="font-semibold text-[30px] text-[#191c1e] tracking-[-0.75px] text-center leading-tight">
              Claim your workspace
            </h1>

            {/* Description */}
            <p className="text-[16px] text-[#464554] text-center max-w-[280px] mt-[12px] leading-relaxed">
              Enter your unique invitation code to join the Cadence ecosystem.
            </p>

            <form onSubmit={handleSubmit} className="w-full mt-[32px]">
              {/* Error banner */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-[8px] p-[12px] flex items-start gap-[8px] mb-[16px]">
                  <AlertCircle className="w-[16px] h-[16px] text-red-500 mt-[2px] shrink-0" />
                  <p className="text-[14px] text-red-700">{error}</p>
                </div>
              )}

              {/* Input with floating label */}
              <div className="relative mb-[16px]">
                <label className="absolute top-[-10px] left-[16px] bg-white px-[4px] text-[12px] font-medium text-[#464554] z-10">
                  Invitation Code
                </label>
                <input
                  type="text"
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                  maxLength={14}
                  placeholder="FLUID-XXXX-XXXX"
                  autoFocus
                  className="w-full bg-[#f2f4f6] rounded-[8px] px-[20px] py-[19px] font-mono text-[16px] tracking-[1.6px] uppercase text-[#191c1e] placeholder:text-[#c7c4d7] outline-none focus:ring-2 focus:ring-[#4648d4]/30 transition-shadow"
                />
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={!inviteCode.trim() || isLoading}
                className="w-full rounded-[8px] py-[16px] px-[24px] text-white font-semibold text-[16px] flex items-center justify-center gap-[8px] shadow-[0px_4px_12px_rgba(70,72,212,0.3)] disabled:opacity-50 disabled:cursor-not-allowed transition-opacity cursor-pointer"
                style={{
                  background: 'linear-gradient(171deg, #4648d4 0%, #6063ee 100%)',
                }}
              >
                {isLoading ? (
                  <Loader2 className="w-[18px] h-[18px] animate-spin" />
                ) : null}
                Continue to GitHub
                {!isLoading && <ArrowRight className="w-[18px] h-[18px]" />}
              </button>
            </form>

            {/* Need an invite link */}
            <div className="pt-[32px]">
              <Link
                to="/login"
                className="text-[14px] font-medium text-[#4648d4] hover:underline"
              >
                Need an invite?
              </Link>
            </div>

            {/* Decorative tagline */}
            <div className="flex items-center gap-[6px] mt-[24px]">
              <div className="w-[6px] h-[6px] rounded-full bg-pink-400" />
              <span className="text-[10px] uppercase tracking-[1px] text-[#464554] opacity-50">
                Precision in Language
              </span>
            </div>
          </div>
        </div>

        {/* Info cards */}
        <div className="w-full max-w-[448px] grid grid-cols-2 gap-[12px] mt-[32px]">
          <div className="bg-[#f2f4f6] rounded-[8px] p-[16px]">
            <span className="font-bold text-[10px] uppercase tracking-[1px] text-[#767586] block mb-[6px]">
              Security
            </span>
            <p className="text-[12px] text-[#464554] leading-relaxed">
              SSO via GitHub Enterprise encryption standard.
            </p>
          </div>
          <div className="bg-[#f2f4f6] rounded-[8px] p-[16px]">
            <span className="font-bold text-[10px] uppercase tracking-[1px] text-[#767586] block mb-[6px]">
              Network
            </span>
            <p className="text-[12px] text-[#464554] leading-relaxed flex items-center gap-[6px]">
              Active Node: AMS-01
              <span className="inline-block w-[6px] h-[6px] rounded-full bg-pink-400" />
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full">
        <div className="mx-auto max-w-[1280px] px-[24px] py-[24px] flex items-center justify-between">
          <span className="font-semibold text-[11px] uppercase tracking-[0.55px] text-[rgba(70,69,84,0.5)]">
            &copy; 2024 Cadence OS.
          </span>
          <div className="flex items-center gap-[24px]">
            <span className="font-semibold text-[11px] uppercase tracking-[0.55px] text-[rgba(70,69,84,0.5)]">
              Privacy Policy
            </span>
            <span className="font-semibold text-[11px] uppercase tracking-[0.55px] text-[rgba(70,69,84,0.5)]">
              Terms of Service
            </span>
            <span className="font-semibold text-[11px] uppercase tracking-[0.55px] text-[rgba(70,69,84,0.5)]">
              Support
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
